import { Check, type LucideIcon } from 'lucide-react';
import { useId } from 'react';

import { MathText } from '@/features/content/MathText';
import { McAnswerDistributionBadges } from '@/features/content/stats/McAnswerDistributionBadges';
import type { UserRole } from '@/features/courses/useCourse';

import { VERDICT_TAGS } from './CheckedAnswerTag';
import { useMathTextAlternative } from './mathTextAlternative';
import type { NormalizedMultipleChoiceOption } from './questionNormalizer';
import { QuestionContent } from './QuestionContent';
import styles from './MultipleChoiceQuestion.module.css';

/**
 * One answer option, shared by the single-select and multi-select controls.
 *
 * Multiple choice and select-all differ in exactly three things: the input type,
 * whether choosing replaces or toggles, and whether more than one option can be
 * marked correct. Everything else — layout, rich-content options, math fallback,
 * distribution badges, the `data-*` attributes that drive correct/incorrect
 * styling, and the answer tags — is identical, so it lives here rather than in
 * two components that would drift.
 *
 * The CSS module is shared for the same reason: the `data-*` contract and the
 * styling that reads it must stay in one place.
 */
interface QuestionOptionRowProps {
  correctIndexForStats?: number;
  disabled: boolean;
  /**
   * The verdict to show on THIS option once an answer has been checked.
   *
   * `submitted` means "the student picked this" with no verdict attached. It
   * exists for select-all, where correctness is a property of the whole
   * selection: colouring each picked option red because the set was wrong would
   * claim every one of them is a wrong option, which is not what was graded.
   */
  checkedAnswerState?: 'correct' | 'incorrect' | 'partial' | 'submitted';
  inputId: string;
  /** `radio` shares a name across the group; `checkbox` does not. */
  inputName: string;
  inputType: 'checkbox' | 'radio';
  isCorrect: boolean;
  isSaved: boolean;
  isSelected: boolean;
  isSubmitted: boolean;
  onToggle(): void;
  option: NormalizedMultipleChoiceOption;
  role: UserRole;
  showCorrectAnswer: boolean;
  showDistributionStats?: boolean;
  stats?: number[];
  units?: string;
}

interface AnswerTag {
  icon?: LucideIcon;
  key: string;
  text: string;
  tone?: 'error' | 'partial' | 'saved' | 'success';
}

const SUBMITTED_TAGS: Record<
  NonNullable<QuestionOptionRowProps['checkedAnswerState']>,
  Omit<AnswerTag, 'key'>
> = {
  ...VERDICT_TAGS,
  // A select-all option before the solutions: picked, with no verdict of its
  // own. The selection's verdict is the line below the options.
  submitted: { text: 'Your answer', tone: 'saved' },
};

export function QuestionOptionRow({
  correctIndexForStats,
  disabled,
  checkedAnswerState,
  inputId,
  inputName,
  inputType,
  isCorrect,
  isSaved,
  isSelected,
  isSubmitted,
  onToggle,
  option,
  role,
  showCorrectAnswer,
  showDistributionStats,
  stats,
  units,
}: QuestionOptionRowProps) {
  const submittedState = isSubmitted ? checkedAnswerState : undefined;
  const nameId = useId();
  const value = useMathTextAlternative<HTMLSpanElement>(
    option.contentUnits ?? option.text ?? option.label,
  );
  const unitsName = useMathTextAlternative<HTMLSpanElement>(units);
  const tags: AnswerTag[] = [];

  if (isSaved) {
    tags.push({ key: 'saved', text: 'Saved', tone: 'saved' });
  }

  if (showCorrectAnswer && isCorrect) {
    tags.push(
      isSubmitted
        ? { key: 'submitted', ...VERDICT_TAGS.correct }
        : { icon: Check, key: 'correct', text: 'Correct answer', tone: 'success' },
    );
  } else if (showCorrectAnswer && isSubmitted) {
    tags.push({ key: 'submitted', ...SUBMITTED_TAGS.incorrect });
  } else if (!showCorrectAnswer && submittedState) {
    tags.push({ key: 'submitted', ...SUBMITTED_TAGS[submittedState] });
  }

  // The input is named by what the option shows: its text, its units and its
  // tags. Rich options are drawn inside an <article>, and axe does not count an
  // article's text as the label of the input beside it, so the wrapping
  // <label> (which keeps the whole row clickable) is not enough on its own.
  // Math is named by its TeX (see mathTextAlternative), kept in `hidden`
  // elements: aria-labelledby still reads an element it points at that is
  // hidden, and browse mode, which reads only what is in the accessibility
  // tree, then reads the equation once, not its TeX after it (issue #103).
  const labelledBy = [
    value.text ? `${nameId}-value-text` : `${nameId}-value`,
    units ? (unitsName.text ? `${nameId}-units-text` : `${nameId}-units`) : null,
    ...tags.map((tag) => `${nameId}-${tag.key}`),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <label
      className={styles.option}
      data-correct-visible={showCorrectAnswer && isCorrect ? 'true' : 'false'}
      data-saved={isSaved ? 'true' : 'false'}
      data-selected={isSelected ? 'true' : 'false'}
      data-submitted-state={submittedState}
      htmlFor={inputId}
    >
      <input
        aria-labelledby={labelledBy}
        checked={isSelected}
        disabled={disabled}
        id={inputId}
        name={inputName}
        onChange={onToggle}
        type={inputType}
      />
      <span className={styles.optionBody}>
        {showDistributionStats ? (
          <McAnswerDistributionBadges
            correctIndex={correctIndexForStats}
            optionIndex={option.index}
            value={stats?.[option.index] ?? 0}
          />
        ) : null}
        <span className={styles.optionValue} id={`${nameId}-value`} ref={value.ref}>
          {option.contentUnits?.length ? (
            <QuestionContent
              headingLevel={4}
              role={role}
              units={option.contentUnits}
            />
          ) : (
            <MathText text={option.text ?? option.label} />
          )}
        </span>
        {value.text ? (
          <span hidden id={`${nameId}-value-text`}>
            {value.text}
          </span>
        ) : null}
        {units ? (
          <span className={styles.optionUnits} id={`${nameId}-units`} ref={unitsName.ref}>
            <MathText text={units} />
          </span>
        ) : null}
        {units && unitsName.text ? (
          <span hidden id={`${nameId}-units-text`}>
            {unitsName.text}
          </span>
        ) : null}
        {tags.map(({ icon: Icon, key, text, tone }) => (
          <span
            className={styles.answerTag}
            data-tone={tone}
            id={`${nameId}-${key}`}
            key={key}
          >
            {Icon ? (
              <Icon aria-hidden="true" className={styles.answerTagIcon} size={14} />
            ) : null}
            {text}
          </span>
        ))}
      </span>
    </label>
  );
}
